import type { SchoolLevel } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';

/**
 * Client admin pour gérer les configurations de la démo anonyme.
 */

export type DemoConfigRow = {
  id: string;
  level: SchoolLevel;
  warmup_exercise_ids: string[];
  main_exercise_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type ExerciseRow = {
  id: string;
  title: string;
  status: 'draft' | 'published' | 'archived';
  is_fundamental: boolean;
  chapter_id: string | null;
  chapter_title: string | null;
  chapter_level: SchoolLevel | null;
  chapter_subject: string | null;
};

const DEMO_CONFIG_COLS =
  'id, level, warmup_exercise_ids, main_exercise_id, is_active, created_at, updated_at';

export async function listDemoConfigs(): Promise<DemoConfigRow[]> {
  const { data, error } = await supabase
    .from('demo_config')
    .select(DEMO_CONFIG_COLS)
    .order('level');
  if (error) throw new Error(error.message);
  return (data ?? []) as DemoConfigRow[];
}

export async function upsertDemoConfig(
  input: Omit<DemoConfigRow, 'id' | 'created_at' | 'updated_at'> & { id?: string },
): Promise<DemoConfigRow> {
  // Si on active cette config, désactiver les autres du même niveau
  // (sinon l'index unique partiel `demo_config_active_per_level_uniq` rejettera).
  if (input.is_active) {
    const filter = input.id ?? '00000000-0000-0000-0000-000000000000';
    await supabase
      .from('demo_config')
      .update({ is_active: false })
      .eq('level', input.level)
      .neq('id', filter);
  }

  const payload = {
    ...(input.id ? { id: input.id } : {}),
    level: input.level,
    warmup_exercise_ids: input.warmup_exercise_ids,
    main_exercise_id: input.main_exercise_id,
    is_active: input.is_active,
  };

  const { data, error } = await supabase
    .from('demo_config')
    .upsert(payload, { onConflict: 'id' })
    .select(DEMO_CONFIG_COLS)
    .single();
  if (error) throw new Error(error.message);
  return data as DemoConfigRow;
}

type RawExerciseRow = {
  id: string;
  title: string;
  status: 'draft' | 'published' | 'archived';
  is_fundamental: boolean;
  chapter_id: string | null;
  chapters: { title: string; level: SchoolLevel; subject: string } | null;
};

function mapExercise(r: RawExerciseRow): ExerciseRow {
  return {
    id: r.id,
    title: r.title,
    status: r.status,
    is_fundamental: r.is_fundamental,
    chapter_id: r.chapter_id,
    chapter_title: r.chapters?.title ?? null,
    chapter_level: r.chapters?.level ?? null,
    chapter_subject: r.chapters?.subject ?? null,
  };
}

async function fetchExercises(opts: {
  fundamentalOnly: boolean;
  level?: SchoolLevel | null;
}): Promise<ExerciseRow[]> {
  let q = supabase
    .from('exercises')
    .select('id, title, status, is_fundamental, chapter_id, chapters(title, level, subject)')
    .eq('status', 'published')
    .order('title');
  if (opts.fundamentalOnly) q = q.eq('is_fundamental', true);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as unknown as RawExerciseRow[];
  const mapped = rows.map(mapExercise);
  return opts.level ? mapped.filter((r) => r.chapter_level === opts.level) : mapped;
}

export function listFundamentalExercises(level?: SchoolLevel | null) {
  return fetchExercises({ fundamentalOnly: true, level });
}

export function listPublishedExercises(level?: SchoolLevel | null) {
  return fetchExercises({ fundamentalOnly: false, level });
}
